/**
 * What the automation would do right now, in plain words: ring (and from which
 * campaign), or why not and until when.
 *
 * The decision is the server's own policy run on the live settings and queue
 * (`auto/policy.ts`), read-only, so this card says exactly what the next tick
 * will decide unless something changes first. Beside it, the facts an operator
 * checks before trusting it: today's calling window in the automation's zone,
 * today's calls against both caps (the mode it calls in now first), and the
 * newest call's start. A readiness gap is listed by name with the way to the
 * settings where it is fixed.
 *
 * The mode is always on the card: TEST rings only test numbers, and a real
 * mode is said just as loudly, because that is the one fact nobody should have
 * to infer. The automation's switch sits at the bottom (managers only).
 */
import { Link } from "react-router-dom";
import { Bot, Loader2 } from "lucide-react";
import { useI18n } from "../../../../i18n/I18nProvider";
import { blockerKey, decisionTone, type AutomationStatus } from "../../../../lib/aiAutomation";
import { StatusPill, fmtNum } from "../../../ui";
import { Panel, Stat } from "../../marketing/shared";
import { Note } from "../../settings/panel/fields";
import { AUTOMATION_SETTINGS_PATH, AutopilotToggle } from "./AutomationControls";
import { decisionKey, fmtWhen, todayCounts, untilKey, viewerTimeZone, windowKey, windowToday } from "./automationView";

export function AutomationDecisionCard({
  status,
  canManage,
  onChanged,
}: {
  status: AutomationStatus;
  canManage: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const { decision, settings, window: win } = status;
  const when = (iso: string | null) => fmtWhen(iso, status.now, locale, settings.timezone, viewerTimeZone());
  const until = untilKey(decision);
  const next = decision.go ? status.campaigns.find((c) => c.id === decision.campaignId) : undefined;
  const today = windowToday(settings.days, win.localDate);
  const holiday = win.holiday ? t(`aiCalls.automation.holiday.${win.holiday}`) : "";

  return (
    <Panel
      title={t("aiCalls.automation.decision.title")}
      description={t(settings.testMode ? "aiCalls.automation.decision.testMode" : "aiCalls.automation.decision.realMode")}
      icon={<Bot className="size-[18px]" strokeWidth={1.7} />}
      actions={
        status.ticking ? (
          <span className="crm-sub mt-0 inline-flex items-center gap-1.5 text-xs">
            <Loader2 className="size-3.5 animate-spin" />
            {t("aiCalls.automation.decision.ticking")}
          </span>
        ) : null
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2" role="status" aria-live="polite">
          <StatusPill tone={decisionTone(decision)} className="text-[13px]">
            {t(decisionKey(decision))}
          </StatusPill>
          {settings.testMode ? (
            <StatusPill tone="info">{t("aiCalls.test")}</StatusPill>
          ) : (
            <StatusPill tone="wt">{t("aiCalls.automation.decision.real")}</StatusPill>
          )}
          {until ? <span className="crm-sub mt-0 text-xs">{t(until, { when: when(decision.until) })}</span> : null}
        </div>

        {next ? (
          <p className="text-[13px]">
            {t("aiCalls.automation.decision.next")}{" "}
            <Link to={`/ai-calls/campaigns/${next.id}`} className="font-medium hover:underline">
              {next.name || t("aiCalls.campaigns.untitled")}
            </Link>
          </p>
        ) : null}

        {status.running > 0 ? <Note tone="info">{t("aiCalls.automation.decision.running", { n: status.running })}</Note> : null}

        {decision.blockers.length > 0 ? (
          <Note tone="bad">
            <span className="block">{t("aiCalls.automation.decision.blockers")}</span>
            <ul className="mt-1 list-disc pl-5">
              {decision.blockers.map((blocker) => (
                <li key={blocker}>{t(blockerKey(blocker))}</li>
              ))}
            </ul>
            {canManage ? (
              <Link to={AUTOMATION_SETTINGS_PATH} className="mt-1 inline-block font-medium underline">
                {t("aiCalls.automation.decision.fix")}
              </Link>
            ) : null}
          </Note>
        ) : null}

        <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
          <Stat
            label={t("aiCalls.automation.window.title")}
            value={today ? `${today.from}-${today.to}` : t("aiCalls.automation.window.todayClosed")}
            tone={win.open ? "good" : "muted"}
            sub={
              <>
                {t(windowKey(win), { holiday })}
                {win.nextOpenAt ? ` · ${t("aiCalls.automation.window.nextOpen", { when: when(win.nextOpenAt) })}` : ""}
              </>
            }
          />
          {todayCounts(status).map((row) => (
            <Stat
              key={row.mode}
              label={t(`aiCalls.automation.today.${row.mode}`)}
              value={t("aiCalls.automation.today.of", { used: fmtNum(row.used), cap: fmtNum(row.cap) })}
              tone={!row.active ? "muted" : row.full ? "warn" : "neutral"}
              sub={row.active ? t("aiCalls.automation.today.active") : t("aiCalls.automation.today.inactive")}
            />
          ))}
          <Stat
            label={t("aiCalls.automation.decision.lastStarted")}
            // The zone is already the tile's sub line, and a big value with "(Europe/Tallinn)" spills out of a phone-width tile.
            value={status.lastStartedAt ? fmtWhen(status.lastStartedAt, status.now, locale, settings.timezone) : "-"}
            tone={status.lastStartedAt ? "neutral" : "muted"}
            sub={t("aiCalls.automation.window.zone", { zone: settings.timezone || "-" })}
          />
        </div>

        <div className="border-t border-[var(--crm-hair)] pt-4">
          <AutopilotToggle autopilot={settings.autopilot} enabled={settings.enabled} canManage={canManage} onChanged={onChanged} />
        </div>
      </div>
    </Panel>
  );
}
